import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useApiClient } from '../api/apiContext';
import { useAuthStore } from '../auth/authStore';
import { isReleased } from '../config/release';
import { visibleNavigation } from '../navigation/navigation';
import { BrandMark } from './BrandMark';
import { Icon } from './Icon';
import { LanguageToggle } from './LanguageToggle';
import { NotificationBell } from './NotificationBell';
import { useOnlineStatus } from './useOnlineStatus';

/**
 * The frame every signed-in screen renders inside: skip link, sidebar, top bar.
 *
 * The sidebar is built from the viewer's permissions, so a receptionist and a clinic administrator
 * see genuinely different products rather than the same menu with disabled entries. Hiding what
 * someone cannot use is not security — the API enforces that — but it is the difference between a
 * tool that fits the job and one that constantly says no.
 *
 * Two things moved in the 2026-08 craft pass, and both are about what belongs where rather than
 * about how anything looks:
 *
 * * **Identity and sign-out moved into the sidebar.** They were in the top bar, which put the
 *   least frequent action any member of staff takes — signing out — at the highest-contrast point
 *   of every screen in the product, as a filled button beside the viewer's name in plain text. In
 *   the sidebar's footer they are where an account always is, and the bar is freed for the thing
 *   it should have been carrying.
 * * **The working context moved into the top bar.** Which clinic you are looking at was previously
 *   answerable only by going to the landing page, and on a product where a doctor can be employed
 *   by two clinics (DEC-012) and an operator can be inside a third on a support grant (SEC-003),
 *   that is the one piece of state that should never be more than a glance away.
 *
 * Neither is an information-architecture change: no destination moved, no permission changed, and
 * the navigation map is the same one the route table is generated from.
 */
export function AppShell() {
  const { t } = useTranslation();
  const client = useApiClient();
  const permissions = useAuthStore((state) => state.permissions);
  const principal = useAuthStore((state) => state.principal);
  const activeClinicId = useAuthStore((state) => state.activeClinicId);
  const signOut = useAuthStore((state) => state.signOut);
  const online = useOnlineStatus();
  const supportSession = useAuthStore((state) => state.supportSession);
  const endSupportSession = useAuthStore((state) => state.endSupportSession);

  const groups = visibleNavigation(permissions);
  const memberships = principal?.memberships ?? [];
  const activeMembership = memberships.find((membership) => membership.clinicId === activeClinicId);
  const roleKey = activeMembership?.role ?? principal?.platformRole;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {t('common.skipToContent')}
      </a>

      {/* An operator working inside a clinic on a grant must never be able to forget it. The
          banner is above the navigation rather than inside the page, so it is present on every
          screen the borrowed token can reach, and it names the clinic and the deadline rather
          than saying "support mode" and leaving both to memory. */}
      {supportSession ? (
        <div className="support-banner" role="status">
          <span className="support-banner__text">
            <Icon name="key" size={18} />
            {t('platformSupport.bannerInside', { clinic: supportSession.clinicLabel })}
            {supportSession.grantExpiresAt ? (
              <>
                {' '}
                <span className="ltr">
                  {t('platformSupport.bannerUntil', {
                    when: supportSession.grantExpiresAt.slice(0, 16).replace('T', ' '),
                  })}
                </span>
              </>
            ) : null}
          </span>
          <button
            type="button"
            className="button button--ghost button--compact"
            onClick={() => {
              void endSupportSession(client);
            }}
          >
            {t('platformSupport.endSession')}
          </button>
        </div>
      ) : null}

      <div className="app-shell__sidebar">
        <div className="sidebar__brand">
          <BrandMark size={26} />
          <span className="wordmark">{t('app.name')}</span>
        </div>

        <nav className="sidebar__nav" aria-label={t('common.mainNavigation')}>
          {groups.map((group) => (
            <div className="nav-group" key={group.key}>
              <p className="nav-group__label">{t(`nav.groups.${group.key}`)}</p>
              {group.items.map((item) => (
                <NavLink key={item.key} to={item.path} className="nav-item">
                  <Icon name={item.icon} size={20} />
                  {t(`nav.items.${item.key}`)}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Who is signed in, their own password, and the way out. The sign-out control keeps its
            own label rather than collapsing into a menu behind the avatar: it is the one action in
            the product somebody may need to find in a hurry on a shared reception machine. */}
        <div className="sidebar__footer">
          <div className="viewer">
            <span className="avatar" aria-hidden="true">
              {initialsOf(principal?.displayName)}
            </span>
            <span className="viewer__identity">
              <span className="viewer__name">{principal?.displayName}</span>
              {roleKey ? <span className="viewer__role">{t(`roles.${roleKey}`, roleKey)}</span> : null}
            </span>
            <NavLink
              to="/account/password"
              className="button button--ghost button--icon"
              style={{ marginInlineStart: 'auto' }}
              aria-label={t('account.changePassword')}
              title={t('account.changePassword')}
            >
              <Icon name="key" size={20} />
            </NavLink>
            <button
              type="button"
              className="button button--ghost button--icon"
              // The name is on the control rather than on the drawing inside it: a title on the
              // svg names the picture, and an assistive technology then has to decide which of the
              // two it is announcing.
              aria-label={t('common.signOut')}
              title={t('common.signOut')}
              onClick={() => {
                void signOut(client);
              }}
            >
              <Icon name="signOut" size={20} />
            </button>
          </div>
        </div>
      </div>

      <header className="app-shell__topbar">
        <div className="topbar__context">
          {activeMembership?.clinicName ? (
            <>
              <span className="avatar avatar--sm" aria-hidden="true">
                {initialsOf(activeMembership.clinicName)}
              </span>
              <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                {activeMembership.clinicName}
              </span>
            </>
          ) : null}
        </div>

        <div className="topbar__tools">
          {/* `navigator.onLine` is a hint about the interface, not about whether Laseera is
              reachable, so this states the fact and gets out of the way rather than blocking
              anything (NFR-010). */}
          {!online ? (
            <span className="banner banner--warning" role="status">
              <Icon name="alert" size={18} />
              {t('states.offline.title')}
            </span>
          ) : null}
          {/* §22's staff rows: a worklist opening, stock running out, a machine due for service.
              In the topbar rather than on a screen, because the person who needs to see them is
              usually looking at something else. Held back until the messaging stage is released,
              because its rows link to screens that may not be reachable yet. */}
          {isReleased('shell.notificationBell') ? <NotificationBell /> : null}
          <LanguageToggle />
        </div>
      </header>

      <main className="app-shell__main" id="main-content" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Up to two initials, for the plate beside a name.
 *
 * Takes the first letter of the first and last words rather than the first two characters, so
 * "Yasmin Abdel Rahman" is YR and not YA — and `Array.from` rather than `slice`, because an Arabic
 * name is not made of one-byte characters and a sliced string can end mid-grapheme.
 */
function initialsOf(name: string | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '';
  }
  const first = Array.from(words[0] ?? '')[0] ?? '';
  const last = words.length > 1 ? (Array.from(words[words.length - 1] ?? '')[0] ?? '') : '';
  return (first + last).toUpperCase();
}
